/* Rhody Reviews api/v1 -- analytics domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request, getToken, buildUrl, ApiError } from './_http';

/* -------------------------------------------------------------------
   Listings health (NAP scan) -- GET /api/v1/listings/nap.

   Matches engine/listings/nap_check.py _build_result() exactly. The score
   is derived ONLY from real fetched data: a platform that is configured but
   unreachable (or unconfigured) is reported as 'unknown'/'not_listed' and is
   NEVER guessed or shown as 0-as-real. The UI must honor that distinction.
   ------------------------------------------------------------------- */
export type ListingsPlatform =
  | 'google'
  | 'foursquare'
  | 'yelp'
  | 'facebook'
  | 'bing'
  | 'apple';
export type NapField = 'name' | 'address' | 'phone';
export type ListingsBand = 'Excellent' | 'Good' | 'Needs work' | 'At risk';
export type FieldStatus = 'consistent' | 'inconsistent';
/* found = a real record came back; unknown = configured but unreachable
   (do NOT render as a real value or 0); not_listed = not configured. */
export type PresenceState = 'found' | 'unknown' | 'not_listed';

export interface ListingsField {
  /* platform -> the normalized value that platform reported. Missing/empty
     string means that platform did not report this field. */
  by_platform: Partial<Record<ListingsPlatform, string>>;
  status: FieldStatus;
  discrepancies: string[];
}

export interface ListingsPerFieldScore {
  earned: number;
  possible: number;
  reporting_platforms: string[];
  status: FieldStatus;
}

export interface ListingsPresenceEntry {
  found: boolean;
  earned: number;
  possible: number;
  state: PresenceState;
}

export interface ListingsDeduction {
  reason: string;
  platform: string;
  points: number;
}

export interface ListingsScoreBreakdown {
  field_consistency: {
    earned: number;
    possible: number;
    per_field: Partial<Record<NapField, ListingsPerFieldScore>>;
  };
  presence: {
    earned: number;
    possible: number;
    per_platform: Partial<Record<ListingsPlatform, ListingsPresenceEntry>>;
  };
  no_gaps: {
    earned: number;
    possible: number;
    deductions: ListingsDeduction[];
  };
}

export interface ListingsSuggestedFix {
  platform: ListingsPlatform;
  field: NapField;
  current: string;
  expected: string;
  /* Deep link to that platform's "edit my info" page. May be empty string. */
  edit_url: string;
}

/* How a directory source is rendered:
   - 'real_scored' : we read a real NAP record from this source's API. When
     state === 'found', nap holds the pulled name/address/phone. When state is
     'unknown', we could not confirm it (e.g. Foursquare before its API key
     lands) -- nap is null and we show the claim_url instead. NEVER fabricate.
   - 'presence_only': sources we cannot read a NAP from (Bing, Apple). nap is
     ALWAYS null -- we never show name/address/phone. We surface only whether
     the listing was found plus a "claim / verify" call to action. */
export type DirectorySourceClass = 'real_scored' | 'presence_only';

export interface DirectoryNap {
  name: string;
  phone: string;
  address: string;
}

export interface DirectoryItem {
  platform: ListingsPlatform;
  label: string;
  source_class: DirectorySourceClass;
  state: PresenceState;
  /* real_scored + found only; presence_only is ALWAYS null. */
  nap: DirectoryNap | null;
  /* owner-facing "claim / verify your listing" deep link. May be ''. */
  claim_url: string;
  presence_earned: number;
  presence_possible: number;
}

export interface ListingsHealth {
  client_id: string | null;
  as_of: string;
  /* platforms a real NAP record came back from. */
  platforms_checked: ListingsPlatform[];
  /* configured but unreachable -> 'unknown' (not a failure, just unconfirmed). */
  platforms_unknown: ListingsPlatform[];
  fields: Partial<Record<NapField, ListingsField>>;
  overall_status: FieldStatus;
  suggested_fixes: ListingsSuggestedFix[];
  score: number; // 0-100
  band: ListingsBand;
  score_breakdown: ListingsScoreBreakdown;
  summary: string;
  /* one row per directory source, fixed order
     [google, foursquare, yelp, facebook, bing, apple]. Drives the directory
     rendering so new sources appear without hardcoding. */
  directory_items: DirectoryItem[];
}

/* Reports metrics (GET /reports) -- the real on-screen Reports body. Same
   honest series the CSV export is built from, plus top-line KPIs and a
   by-platform split. KPIs that have no real source (avg reply time, review
   themes) are intentionally absent; the screen must not invent them. avg_rating
   and reply_rate are null when there is no data yet. */
export interface ReportsSeriesPoint {
  period: string;
  label: string;
  value: number | null;
  [k: string]: unknown;
}
export interface ReportsSeries {
  client_id?: string;
  series: ReportsSeriesPoint[];
  meta?: Record<string, unknown>;
}
export interface ReportsKpis {
  avg_rating: number | null;
  avg_rating_n: number;
  new_reviews: number;
  new_reviews_window_weeks: number;
  reply_rate: number | null; // fraction 0..1
  reply_rate_replied: number;
  reply_rate_total: number;
  total_reviews: number;
}
export interface ReportsPlatformSplit {
  platform: string;
  count: number;
  pct: number;
}
export interface ReportsResponse {
  client_id: string;
  kpis: ReportsKpis;
  review_count_by_week: ReportsSeries;
  avg_rating_trend: ReportsSeries;
  response_rate_by_month: ReportsSeries;
  by_platform: ReportsPlatformSplit[];
  as_of: string;
}

/* -------------------------------------------------------------------
   Activity heatmap + gamification -- GET /api/v1/activity. Matches
   activity_summary() in engine/analytics/activity.py. One cell per day
   across the trailing window (zeros included, no gaps), plus a
   gamification block derived from the same real rows. Powers the
   Dashboard contribution graph + the streak/badges/goal panel.
   ------------------------------------------------------------------- */
export interface ActivityDay {
  date: string; // YYYY-MM-DD (UTC)
  count: number; // reviews received that day (drives heatmap intensity)
  reviews: number;
  requests: number;
}
export interface ActivityMilestones {
  earned: number[];
  next: number | null;
  current: number;
  to_next: number;
  progress_pct: number;
}
export interface ActivityMonthlyGoal {
  count: number;
  target: number;
  pct: number;
  basis: 'configured' | 'last_month' | 'default';
}
/* Response-time streak: consecutive reviews replied to within 24h. tracked is
   false when no reply timestamps exist yet -- render soft / omit, never as a
   0-streak failure. */
export interface ActivityResponseStreak {
  count: number;
  best: number;
  tracked: boolean;
}
/* Streak-break risk: is an active review streak quiet enough to be about to
   lapse? Drives the "send a request to keep your streak alive" nudge. */
export interface ActivityStreakBreak {
  at_risk: boolean;
  days_since_last: number | null;
  streak_kind: 'week' | 'day' | null;
  grace_days: number;
}
export interface ActivityGamification {
  streak_weeks: number;
  streak_days: number;
  total_reviews: number;
  milestones: ActivityMilestones;
  monthly_goal: ActivityMonthlyGoal;
  this_week: number;
  last_week: number;
  week_delta: number;
  response_streak: ActivityResponseStreak;
  streak_break: ActivityStreakBreak;
}
export interface ActivityResponse {
  client_id: string;
  days: ActivityDay[];
  total: number;
  max: number;
  window_days: number;
  start_date: string;
  end_date: string;
  as_of: string;
  gamification: ActivityGamification;
}

export const analyticsApi = {
  /* Scored NAP / listings-health scan for the signed-in client. A platform
     whose credentials are missing or whose fetch fails comes back 'unknown'
     -- never guessed. Powers the in-product Listings health panel. */
  getListingsHealth(signal?: AbortSignal): Promise<ListingsHealth> {
    return request<ListingsHealth>('/api/v1/listings/nap', { signal });
  },

  /* Per-day activity heatmap + gamification for the Dashboard. days (server
     default 364 = 52 weeks, max 366) sets the trailing window. Every figure
     is derived from real rows; empty days are count 0 and metrics with no
     signal are 0/None -- never fabricated. */
  getActivity(days?: number, signal?: AbortSignal): Promise<ActivityResponse> {
    return request<ActivityResponse>('/api/v1/activity', {
      query: { days },
      signal,
    });
  },

  /* ----- Reports metrics (JSON, on-screen body) ----- */
  getReports(signal?: AbortSignal): Promise<ReportsResponse> {
    return request<ReportsResponse>('/api/v1/reports', { signal });
  },

  /* ----- Reports export (CSV) -----
     Fetches the CSV with the Bearer token attached (a plain anchor href
     cannot send the Authorization header), then returns the raw text so the
     caller can trigger a Blob download. */
  async exportReportsCsv(signal?: AbortSignal): Promise<string> {
    const token = getToken();
    const headers: Record<string, string> = { Accept: 'text/csv' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(buildUrl('/api/v1/reports/export'), { headers, signal });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new ApiError(res.status, `http_${res.status}`, text || undefined);
    }
    return res.text();
  },
};
